import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface AuthUser {
  id: number
  email: string
  firstName: string
  lastName: string
  phone: string
}

export interface TokenMeta {
  value: string
  exp: number // Unix timestamp in seconds (as returned by JWT)
}

interface JwtPayload {
  sub: string
  userId: number
  fname: string
  lname: string
  phone: string
  authorities: string
  appContext: string
  iss: string
  iat: number
  exp: number
}

function decodeJwt(token: string): JwtPayload {
  const payloadBase64 = token.split('.')[1]
  // Replace URL-safe Base64 chars and pad if needed
  const padded = payloadBase64?.replace(/-/g, '+').replace(/_/g, '/').padEnd(
    payloadBase64.length + (4 - (payloadBase64.length % 4)) % 4,
    '='
  )
  const binaryString = atob(padded || '')
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  const utf8String = new TextDecoder('utf-8').decode(bytes)
  return JSON.parse(utf8String) as JwtPayload
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const token = ref<TokenMeta | null>(null)

  const isLogged = computed<boolean>(() =>
    token.value !== null && token.value.exp * 1000 > Date.now()
  )

  const userId = computed<number | null>(() => {
    if (!token.value) return null
    try {
      return decodeJwt(token.value.value).userId
    }
    catch {
      return null
    }
  })

  function setToken(rawToken: string): void {
    const payload = decodeJwt(rawToken)
    user.value = {
      id: payload.userId,
      email: payload.sub,
      firstName: payload.fname,
      lastName: payload.lname,
      phone: payload.phone,
    }
    token.value = {
      value: rawToken,
      exp: payload.exp,
    }
  }

  function logout(): void {
    user.value = null
    token.value = null
  }

  return { user, token, isLogged, userId, setToken, logout }
}, { persist: true })

export type AuthStore = ReturnType<typeof useAuthStore>
